import { useCallback, useEffect, useState } from 'react';
import { notifications, type Notification } from '../data/notifications';

const COOKIE_NAME = 'polaris_read_notifications';
const COOKIE_MAX_AGE_DAYS = 365;

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const target = name + '=';
  const parts = document.cookie.split('; ');
  for (const part of parts) {
    if (part.startsWith(target)) return decodeURIComponent(part.slice(target.length));
  }
  return null;
}

function writeCookie(name: string, value: string, maxAgeDays: number) {
  if (typeof document === 'undefined') return;
  const maxAge = maxAgeDays * 24 * 60 * 60;
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAge}; SameSite=Lax`;
}

function loadReadIds(): Set<string> {
  const raw = readCookie(COOKIE_NAME);
  if (!raw) return new Set();
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return new Set(parsed.filter((x): x is string => typeof x === 'string'));
  } catch {
    // fall through
  }
  return new Set();
}

function saveReadIds(ids: Set<string>) {
  writeCookie(COOKIE_NAME, JSON.stringify(Array.from(ids)), COOKIE_MAX_AGE_DAYS);
}

export interface UseNotificationsResult {
  items: Notification[];
  readIds: Set<string>;
  unreadCount: number;
  markRead: (id: string) => void;
  markAllRead: () => void;
}

export function useNotifications(): UseNotificationsResult {
  const [readIds, setReadIds] = useState<Set<string>>(() => loadReadIds());

  useEffect(() => {
    // Prune cookie of ids no longer present in the source list so the cookie
    // does not grow unbounded as old notifications are removed from the file.
    const valid = new Set(notifications.map(n => n.id));
    let changed = false;
    const pruned = new Set<string>();
    for (const id of readIds) {
      if (valid.has(id)) pruned.add(id);
      else changed = true;
    }
    if (changed) {
      setReadIds(pruned);
      saveReadIds(pruned);
    }
  }, []);

  const markRead = useCallback((id: string) => {
    setReadIds(prev => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      saveReadIds(next);
      return next;
    });
  }, []);

  const markAllRead = useCallback(() => {
    setReadIds(prev => {
      const next = new Set(prev);
      for (const n of notifications) next.add(n.id);
      if (next.size === prev.size) return prev;
      saveReadIds(next);
      return next;
    });
  }, []);

  const items = [...notifications].sort((a, b) => (a.date < b.date ? 1 : -1));
  const unreadCount = items.reduce((acc, n) => acc + (readIds.has(n.id) ? 0 : 1), 0);

  return { items, readIds, unreadCount, markRead, markAllRead };
}
